import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  Card,
  CardContent,
  Divider,
  Chip,
  IconButton,
  useTheme,
  alpha,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CalendarMonth as CalendarIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { getGLAccountById } from '../firebase/glAccountServices';
import { getGLTransactionsByAccountId, GLTransaction, GLTransactionEntry } from '../firebase/glTransactionServices';
import { Timestamp } from 'firebase/firestore';

// Format currency helper function
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-MY', {
    style: 'currency',
    currency: 'MYR',
    minimumFractionDigits: 2
  }).format(amount);
};

// Format date helper function
const formatDate = (date: Date | Timestamp): string => {
  if (date instanceof Date) {
    return format(date, 'dd MMM yyyy');
  } else if (date) {
    // For Firestore Timestamp objects
    return format(date.toDate(), 'dd MMM yyyy');
  }
  return '';
};

const GLAccountTransactions: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { accountId, startDate: startDateParam, endDate: endDateParam } = useParams<{
    accountId: string;
    startDate?: string;
    endDate?: string;
  }>();
  
  const [account, setAccount] = useState<any>(null);
  const [transactions, setTransactions] = useState<GLTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalDebits, setTotalDebits] = useState(0);
  const [totalCredits, setTotalCredits] = useState(0);
  
  useEffect(() => {
    const fetchData = async () => {
      if (!accountId) return;
      
      setLoading(true);
      try {
        // Get account details
        const accountData = await getGLAccountById(accountId);
        setAccount(accountData);
        
        // Parse dates if provided
        const startDate = startDateParam ? new Date(startDateParam) : null;
        const endDate = endDateParam ? new Date(endDateParam) : null;
        
        // Get transactions for this account
        const transactionsData = await getGLTransactionsByAccountId(
          accountId,
          startDate,
          endDate,
          true // Set to true to get full transactions
        );
        
        setTransactions(transactionsData);
        
        // Calculate totals
        let debits = 0;
        let credits = 0;
        
        transactionsData.forEach(transaction => {
          transaction.entries.forEach(entry => {
            if (entry.accountId === accountId) {
              debits += entry.debit;
              credits += entry.credit;
            }
          });
        });
        
        setTotalDebits(debits);
        setTotalCredits(credits);
      } catch (error) {
        console.error('Error fetching GL account transactions:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [accountId, startDateParam, endDateParam]);
  
  const handleBack = () => {
    // Navigate to Reports page with Trial Balance tab (index 5) selected
    navigate('/reports?tab=5');
  };
  
  const getNetAmount = (transaction: GLTransaction): { amount: number; isDebit: boolean } => {
    const entry = transaction.entries.find(e => e.accountId === accountId);
    if (!entry) return { amount: 0, isDebit: false };
    
    if (entry.debit > 0) {
      return { amount: entry.debit, isDebit: true };
    } else {
      return { amount: entry.credit, isDebit: false };
    }
  };
  
  const getTransactionDescription = (transaction: GLTransaction): string => {
    // Get the other side of the entry for better description
    const otherEntries = transaction.entries.filter(e => e.accountId !== accountId);
    if (otherEntries.length === 0) return transaction.description;
    
    if (otherEntries.length === 1) {
      return `${transaction.description} - ${otherEntries[0].accountName}`;
    } else {
      return `${transaction.description} - Multiple accounts`;
    }
  };
  
  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={handleBack} color="primary">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1" fontWeight={600}>
          GL Account Transactions
        </Typography>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : account ? (
        <>
          {/* Account Info Card */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Account Number</Typography>
                  <Typography variant="h6">{account.accountNumber}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary">Account Name</Typography>
                  <Typography variant="h6">{account.accountName}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Type</Typography>
                  <Chip 
                    label={account.accountType} 
                    color={account.balanceType === 'debit' ? 'primary' : 'secondary'}
                    size="small"
                  />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Current Balance</Typography>
                  <Typography 
                    variant="h6" 
                    color={account.currentBalance > 0 ? 'success.main' : 'error.main'}
                  >
                    {formatCurrency(account.currentBalance)}
                  </Typography>
                </Box>
              </Box>
              
              {startDateParam && endDateParam && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {`Period: ${formatDate(new Date(startDateParam))} - ${formatDate(new Date(endDateParam))}`}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
          
          {/* Transactions Table */}
          <Typography variant="h6" sx={{ mb: 2 }}>Transaction History</Typography>
          
          {transactions.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No transactions found for this account in the selected period.
              </Typography>
            </Paper>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined" sx={{ 
                borderRadius: 1,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                overflow: 'auto',
                width: '100%',
                mb: 3
              }}>
                <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow sx={{ 
                      backgroundColor: theme.palette.primary.main,
                      '& th': { 
                        color: theme.palette.primary.contrastText,
                        fontWeight: 'bold',
                        py: 2,
                        px: 2,
                        whiteSpace: 'nowrap'
                      }
                    }}>
                      <TableCell sx={{ width: '15%', pl: 3 }}>Date</TableCell>
                      <TableCell sx={{ width: '40%' }}>Description</TableCell>
                      <TableCell sx={{ width: '15%' }}>Reference</TableCell>
                      <TableCell align="right" sx={{ width: '15%' }}>Debit</TableCell>
                      <TableCell align="right" sx={{ width: '15%', pr: 3 }}>Credit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.map((transaction) => {
                      // Filter entries for the current account only
                      const accountEntries = transaction.entries.filter(entry => entry.accountId === accountId);
                      
                      if (accountEntries.length === 0) return null;
                      
                      return (
                        <React.Fragment key={transaction.id}>
                          {/* Transaction header row */}
                          <TableRow sx={{ 
                            backgroundColor: alpha(theme.palette.primary.light, 0.1),
                            '&:hover': { backgroundColor: alpha(theme.palette.primary.light, 0.15) }
                          }}>
                            <TableCell sx={{ pl: 3 }}>{formatDate(transaction.transactionDate)}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="body2" fontWeight="bold">
                                  {transaction.description}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {transaction.referenceType === 'invoice' && (
                                  <Tooltip title="Invoice">
                                    <ReceiptIcon fontSize="small" color="primary" />
                                  </Tooltip>
                                )}
                                {transaction.referenceType === 'expense' && (
                                  <Tooltip title="Expense">
                                    <AccountBalanceIcon fontSize="small" color="primary" />
                                  </Tooltip>
                                )}
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  {transaction.referenceId}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right"></TableCell>
                            <TableCell align="right" sx={{ pr: 3 }}></TableCell>
                          </TableRow>
                          
                          {/* Show counter GL account entries */}
                          {accountEntries.map((entry, entryIndex) => {
                            // Find counter entry (the other side of the transaction)
                            const counterEntries = transaction.entries.filter(e => e.accountId !== accountId);
                            let counterDescription = '';
                            
                            // Check if this is a COGS/Write-off transaction
                            const isCogsWriteoffTransaction = transaction.referenceType === 'monthly_cogs';
                            
                            if (isCogsWriteoffTransaction) {
                              // For COGS/Write-off transactions, determine if this is the COGS or Write-off part
                              if (entry.credit > 0 && entry.accountNumber === '1300002') {
                                // This is a credit to Inventory Balances
                                // Check if this specific entry is for COGS or Write-off
                                
                                // Find the corresponding debit entry that matches this credit amount
                                const matchingDebit = transaction.entries.find(e => 
                                  e.debit === entry.credit && e.accountId !== accountId
                                );
                                
                                if (matchingDebit) {
                                  if (matchingDebit.accountNumber.startsWith('81')) {
                                    // This is a COGS entry
                                    counterDescription = `${matchingDebit.accountNumber} - ${matchingDebit.accountName} (COGS)`;
                                  } else if (matchingDebit.accountNumber.startsWith('73')) {
                                    // This is a Write-off entry
                                    counterDescription = `${matchingDebit.accountNumber} - ${matchingDebit.accountName} (Write-off)`;
                                  } else {
                                    counterDescription = `${matchingDebit.accountNumber} - ${matchingDebit.accountName}`;
                                  }
                                } else {
                                  // If we can't find a matching debit, fall back to showing all counter accounts
                                  const cogsEntries = counterEntries.filter(e => e.accountNumber.startsWith('81'));
                                  const writeOffEntries = counterEntries.filter(e => e.accountNumber.startsWith('73'));
                                  
                                  if (cogsEntries.length > 0 && writeOffEntries.length === 0) {
                                    counterDescription = `${cogsEntries[0].accountNumber} - ${cogsEntries[0].accountName} (COGS)`;
                                  } else if (writeOffEntries.length > 0 && cogsEntries.length === 0) {
                                    counterDescription = `${writeOffEntries[0].accountNumber} - ${writeOffEntries[0].accountName} (Write-off)`;
                                  } else if (cogsEntries.length > 0 && writeOffEntries.length > 0) {
                                    // If both types exist, we need to determine which one this entry corresponds to
                                    // For now, just show the first counter account
                                    counterDescription = `${counterEntries[0].accountNumber} - ${counterEntries[0].accountName}`;
                                  } else {
                                    counterDescription = `Unknown source account`;
                                  }
                                }
                              } else if (entry.debit > 0 && (entry.accountNumber === '8100001' || entry.accountNumber === '7300001')) {
                                // This is a debit to COGS or Write-off account
                                // Show the inventory account it's coming from
                                const inventoryEntries = counterEntries.filter(e => e.accountNumber === '1300002');
                                if (inventoryEntries.length > 0) {
                                  counterDescription = `${inventoryEntries[0].accountNumber} - ${inventoryEntries[0].accountName}`;
                                } else {
                                  counterDescription = `Unknown inventory account`;
                                }
                              } else {
                                // Fallback
                                counterDescription = `${entry.accountNumber} - ${entry.accountName}`;
                              }
                            } else {
                              // For regular transactions
                              if (counterEntries.length === 1) {
                                // Single counter account
                                counterDescription = `${counterEntries[0].accountNumber} - ${counterEntries[0].accountName}`;
                              } else if (counterEntries.length > 1) {
                                // Multiple counter accounts - show the first one with indication
                                counterDescription = `${counterEntries[0].accountNumber} - ${counterEntries[0].accountName} + ${counterEntries.length - 1} more`;
                              } else {
                                // Fallback to current account if no counter entries
                                counterDescription = `${entry.accountNumber} - ${entry.accountName}`;
                              }
                            }
                            
                            return (
                              <TableRow key={`${transaction.id}-${entryIndex}`} sx={{ 
                                '&:nth-of-type(odd)': { backgroundColor: alpha(theme.palette.grey[100], 0.5) },
                                '&:hover': { backgroundColor: alpha(theme.palette.primary.light, 0.05) }
                              }}>
                                <TableCell sx={{ pl: 3 }}></TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {counterDescription}
                                  </Typography>
                                </TableCell>
                                <TableCell></TableCell>
                                <TableCell align="right">
                                  {entry.debit > 0 && (
                                    <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'medium' }}>
                                      {formatCurrency(entry.debit)}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell align="right" sx={{ pr: 3 }}>
                                  {entry.credit > 0 && (
                                    <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 'medium' }}>
                                      {formatCurrency(entry.credit)}
                                    </Typography>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                    
                    {/* Totals Row */}
                    <TableRow sx={{ 
                      backgroundColor: alpha(theme.palette.primary.light, 0.1),
                      fontWeight: 'bold',
                      '& td': { 
                        borderTop: `1px solid ${theme.palette.divider}`,
                        py: 2
                      }
                    }}>
                      <TableCell colSpan={3} sx={{ pl: 3 }}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          TOTAL
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle2" color="success.main" fontWeight="bold">
                          {formatCurrency(totalDebits)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ pr: 3 }}>
                        <Typography variant="subtitle2" color="error.main" fontWeight="bold">
                          {formatCurrency(totalCredits)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    
                    {/* Net Change Row */}
                    <TableRow sx={{ backgroundColor: alpha(theme.palette.grey[100], 0.8) }}>
                      <TableCell colSpan={3} align="right" sx={{ pr: 3 }}>
                        <Typography variant="body2" color="text.secondary" fontWeight="medium">
                          Net Change
                        </Typography>
                      </TableCell>
                      <TableCell colSpan={2} align="right" sx={{ pr: 3 }}>
                        <Typography 
                          variant="body2" 
                          color={account.balanceType === 'debit' 
                            ? (totalDebits > totalCredits ? 'success.main' : 'error.main')
                            : (totalCredits > totalDebits ? 'success.main' : 'error.main')
                          }
                          fontWeight="medium"
                        >
                          {formatCurrency(Math.abs(totalDebits - totalCredits))}
                          {account.balanceType === 'debit' 
                            ? (totalDebits > totalCredits ? ' DR' : ' CR')
                            : (totalCredits > totalDebits ? ' CR' : ' DR')
                          }
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Account not found.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default GLAccountTransactions;
